import type { EmailImapFolder } from '../useData';

export interface ImapFolderTreeNode {
  folder: EmailImapFolder;
  children: ImapFolderTreeNode[];
}

function sortFolders(a: EmailImapFolder, b: EmailImapFolder): number {
  if (a.imapPath === 'INBOX') return -1;
  if (b.imapPath === 'INBOX') return 1;
  const la = (a.displayName || a.imapPath).toLowerCase();
  const lb = (b.displayName || b.imapPath).toLowerCase();
  return la.localeCompare(lb, undefined, { sensitivity: 'base' });
}

/**
 * Build a tree from flat IMAP LIST rows using parentPath (and imapPath presence in the set).
 */
export function buildImapFolderTree(folders: EmailImapFolder[]): ImapFolderTreeNode[] {
  const list = folders.filter((f) => f.selectable && f.presentOnServer);
  const byPath = new Map(list.map((f) => [f.imapPath, f]));

  const childrenOf = new Map<string | null, EmailImapFolder[]>();
  for (const f of list) {
    let parentKey: string | null = null;
    if (f.parentPath && byPath.has(f.parentPath)) {
      parentKey = f.parentPath;
    }
    if (!childrenOf.has(parentKey)) childrenOf.set(parentKey, []);
    childrenOf.get(parentKey)!.push(f);
  }

  // Use Map.forEach — avoids `for..of` on Map (needs downlevelIteration with older TS targets).
  childrenOf.forEach((arr) => {
    arr.sort(sortFolders);
  });

  function build(parentKey: string | null): ImapFolderTreeNode[] {
    const kids = childrenOf.get(parentKey) ?? [];
    return kids.map((folder) => ({
      folder,
      children: build(folder.imapPath),
    }));
  }

  return build(null);
}
